import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Provider Context for managing cloud provider filter state.
 * Implements REQ-012: Provider-specific filtering with URL parameter sync + localStorage persistence.
 *
 * Features:
 * - Syncs with URL search parameter ?provider= for shareable links
 * - Persists selection in localStorage so it's remembered across sessions
 * - URL parameter takes priority over localStorage (shareable links work correctly)
 * - Defaults to "all" when neither URL param nor localStorage has a valid value
 * - Supports: 'all' | 'aws' | 'azure' | 'gcp'
 * - Provides { provider, setProvider } to consuming components via useProvider() hook
 */

export type CloudProvider = 'all' | 'aws' | 'azure' | 'gcp';

const VALID_PROVIDERS: CloudProvider[] = ['all', 'aws', 'azure', 'gcp'];
const DEFAULT_PROVIDER: CloudProvider = 'all';
const URL_PARAM_KEY = 'provider';
const STORAGE_KEY = 'finops_provider_preference';

function isValidProvider(value: string | null): value is CloudProvider {
  return value !== null && VALID_PROVIDERS.includes(value as CloudProvider);
}

/**
 * Safely read from localStorage. Returns null on any error
 * (e.g., Safari private mode, quota exceeded, disabled storage).
 */
function getStoredProvider(): CloudProvider | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidProvider(stored)) {
      return stored;
    }
    return null;
  } catch {
    // localStorage may throw in Safari private mode or when disabled
    return null;
  }
}

/**
 * Safely write to localStorage. Silently fails on errors
 * (e.g., Safari private mode, quota exceeded, disabled storage).
 */
function setStoredProvider(provider: CloudProvider): void {
  try {
    if (provider === DEFAULT_PROVIDER) {
      // Clear storage when set to default to keep things clean
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, provider);
    }
  } catch {
    // Silently ignore localStorage errors (Safari private mode, etc.)
  }
}

export interface ProviderContextValue {
  /** The currently selected cloud provider filter */
  provider: CloudProvider;
  /** Update the selected cloud provider (also updates URL param and localStorage) */
  setProvider: (provider: CloudProvider) => void;
}

const ProviderContext = createContext<ProviderContextValue | undefined>(undefined);

interface ProviderProviderProps {
  children: React.ReactNode;
}

/**
 * ProviderProvider component that wraps dashboard routes.
 * 
 * Priority order for initial provider value:
 * 1. URL parameter ?provider= (highest priority - for shareable links)
 * 2. localStorage value (remembered from previous session)
 * 3. Default "all" (fallback)
 *
 * Must be rendered inside a <BrowserRouter> (or similar router) since it uses useSearchParams.
 */
export const ProviderProvider: React.FC<ProviderProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // `provider` is held in real state (not re-derived from searchParams/localStorage
  // on every render) - two back-to-back setProvider calls would otherwise race,
  // since setSearchParams's own commit is async and a second call's functional
  // updater could read a stale `prev`, silently dropping the second click's change.
  // Initial value still honors the same URL > localStorage > default priority.
  const [provider, setProviderState] = useState<CloudProvider>(() => {
    const paramValue = searchParams.get(URL_PARAM_KEY);
    if (isValidProvider(paramValue)) {
      return paramValue;
    }
    const storedValue = getStoredProvider();
    if (storedValue) {
      return storedValue;
    }
    return DEFAULT_PROVIDER;
  });

  const setProvider = useCallback(
    (newProvider: CloudProvider) => {
      setProviderState(newProvider);
      setStoredProvider(newProvider);

      // Update URL parameter (best-effort for shareable links - state above is authoritative)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newProvider === DEFAULT_PROVIDER) {
            next.delete(URL_PARAM_KEY);
          } else {
            next.set(URL_PARAM_KEY, newProvider);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const value = useMemo<ProviderContextValue>(
    () => ({ provider, setProvider }),
    [provider, setProvider]
  );

  return (
    <ProviderContext.Provider value={value}>
      {children}
    </ProviderContext.Provider>
  );
};

/**
 * Custom hook to consume the provider context.
 * Returns { provider, setProvider }.
 *
 * @throws Error if used outside of a ProviderProvider
 *
 * @example
 * ```tsx
 * const { provider, setProvider } = useProvider();
 * // provider === 'all' | 'aws' | 'azure' | 'gcp'
 * setProvider('aws'); // updates context + URL param
 * ```
 */
export function useProvider(): ProviderContextValue {
  const context = useContext(ProviderContext);
  if (context === undefined) {
    throw new Error('useProvider must be used within a ProviderProvider');
  }
  return context;
}
